import React, { useState } from 'react';
import styles from './styles.module.scss';
import AbrdnSelect, { SelectOption } from 'components/Generic/Forms/FormFields/AbrdnSelect';
import { ShareClass } from 'model/KidsHub';
import { useGetPredictivePerformanceDataQuery } from '@/store/kidshub/services/kidshubApi';
import { useConstant } from 'hooks/useConstant';
import { useI18n } from 'next-localization';
import { ModalContent } from 'components/Modals/ModalContent';
import { ModalHeader } from 'components/Modals/ModalHeader';
import { ModalBody } from 'components/Modals/ModalBody';
import classnames from 'classnames';
import { useSitecoreContext } from 'hooks/useSitecoreContext';

type PredictivePerformanceProps = {
  shareClass: ShareClass;
  countryCode: string;
  onCloseModal: () => void;
};

const PredictivePerformance: React.FC<PredictivePerformanceProps> = ({
  shareClass,
  countryCode,
  onCloseModal,
}) => {
  const [selectedYear, setSelectedYear] = useState<number | undefined>();
  const [selectedMonth, setSelectedMonth] = useState<number | undefined>();
  const lowestPossibleYear = 2022;
  const { t } = useI18n();
  const {
    sitecoreContext: { language },
  } = useSitecoreContext();

  const monthsList = [
    { name: t('dateMonthJan'), value: 1 },
    { name: t('dateMonthFeb'), value: 2 },
    { name: t('dateMonthMar'), value: 3 },
    { name: t('dateMonthApr'), value: 4 },
    { name: t('dateMonthMay'), value: 5 },
    { name: t('dateMonthJun'), value: 6 },
    { name: t('dateMonthJul'), value: 7 },
    { name: t('dateMonthAug'), value: 8 },
    { name: t('dateMonthSep'), value: 9 },
    { name: t('dateMonthOct'), value: 10 },
    { name: t('dateMonthNov'), value: 11 },
    { name: t('dateMonthDec'), value: 12 },
  ];

  const { data, error } = useGetPredictivePerformanceDataQuery({
    shareClassId: shareClass.shareclassID,
    language: language ?? 'en',
    countryCode: countryCode,
    month: String(selectedMonth),
    year: String(selectedYear),
  });
  if (!!error) console.error({ error });

  const performanceSets = Object.values(data?.performanceSets ?? {}).filter((set) => !!set);
  const allPeriods = performanceSets[0]?.scenarios[0].periods ?? [];
  const recommendedPeriod =
    allPeriods.find((period) => period.performanceTimePeriod.includes('Recommended')) ??
    allPeriods.at(-1);
  const [, recommendedPeriodText] =
    recommendedPeriod?.performanceTimePeriod.match(/\((.*)\)/) ?? [];

  const currencyType = useConstant<string | undefined>(
    () => data?.performanceSets['stressScenario']?.scenarios[0].periods[0]?.value.split(' ')[1]
  );
  const lastRelevantDate = useConstant(() =>
    data?.lastRelevantDate ? new Date(data.lastRelevantDate) : undefined
  );
  const years = useConstant(() => {
    return !!lastRelevantDate
      ? Array.from(
          { length: lastRelevantDate.getFullYear() - lowestPossibleYear + 1 },
          (_, i) => lowestPossibleYear + i
        )
      : undefined;
  });

  const handleSelectYear = ({ value }: SelectOption<number>) => {
    setSelectedYear(value);
    if (value < (lastRelevantDate?.getFullYear() ?? 0)) {
      setSelectedMonth(12);
    }
  };

  const handleSelectMonth = ({ value }: SelectOption<number>) => {
    setSelectedMonth(value);
  };

  const getDefaultYearOption = (): SelectOption<number> | undefined => {
    if (!years || !lastRelevantDate) return;
    const lastYear = years.filter((year) => lastRelevantDate.getFullYear() >= year).at(-1);
    return lastYear ? { label: String(lastYear), value: lastYear } : undefined;
  };

  const getDefaultMonthOption = (): SelectOption<number> | undefined => {
    if (!lastRelevantDate) return;
    const lastMonth = lastRelevantDate.getMonth() + 1;
    const month = monthsList.find((m) => m.value === lastMonth);
    if (!month) return;
    return { label: month.name, value: month.value };
  };

  return (
    <ModalContent>
      <ModalHeader title={t('performanceScenarioCalculations')} onClose={onCloseModal} />

      {data && (
        <p className={`${styles.fieldText} field-text pt-5 px-2 px-md-4`}>
          {data?.recommendedHoldingPeriod}
        </p>
      )}
      <p className={`${styles.fieldText} field-text pb-4 px-2 px-md-4`}>
        {t('exampleInvestment')}: 10,000 {currencyType}
      </p>

      <div className="d-flex flex-column flex-md-row w-100 px-2 px-md-4">
        <div className={`${styles.monthSelector} w-100 me-2 mb-2 mb-md-0`}>
          <p className={`${styles.formLabel} ${styles.capitalize} mb-1`}>{t('month')}</p>
          <AbrdnSelect
            // enforcing a re-mount since the `defaultValue` prop change is not supported
            key={`${lastRelevantDate}`}
            options={monthsList.map((month) => ({
              label: month.name,
              value: month.value,
            }))}
            onChange={handleSelectMonth}
            placeholder={t('chooseAnOption')}
            defaultValue={getDefaultMonthOption()}
          />
        </div>

        <div className={`${styles.yearSelector} w-100`}>
          <p id="local" className={`${styles.formLabel} ${styles.capitalize} mb-1`}>
            {t('year')}
          </p>
          <AbrdnSelect
            // enforcing a re-mount since the `defaultValue` prop change is not supported
            key={`${lastRelevantDate}`}
            options={
              years?.map((year) => ({
                label: String(year),
                value: year,
              })) ?? []
            }
            onChange={handleSelectYear}
            placeholder={t('chooseAnOption')}
            defaultValue={getDefaultYearOption()}
          />
        </div>
      </div>

      <ModalBody>
        {data && (
          <table
            className={`d-flex flex-column ${styles.table}`}
            aria-label="Predictive Performance Table"
          >
            <thead>
              <tr className={`py-2 pe-md-2 ${styles.tr}`}>
                <th className={`section-header-small`}>{t('scenarios')}</th>
                {performanceSets.length > 0 && (
                  <th className={` section-header-small`}>
                    {allPeriods.map((period) => (
                      <p
                        key={`${period.performanceTimePeriodID}-years`}
                        className={styles.relative}
                      >
                        {period.performanceTimePeriod.replace(/\(.*\)/, '')}
                        {period.performanceTimePeriodID ===
                          recommendedPeriod?.performanceTimePeriodID && (
                          <span className={styles.recommendedYear}>{recommendedPeriodText}</span>
                        )}
                      </p>
                    ))}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {performanceSets.map((set, setIndex) => (
                <tr
                  key={`${set.scenarioType}-${setIndex}`}
                  className={`ps-xl-2 flex-column flex-xl-row py-3 ${styles.tr}`}
                >
                  <td className={`${styles.fieldText} field-text ps-md-2 mb-0 mb-md-1 mb-xl-0`}>
                    {set.scenarioType}
                  </td>
                  <td className={`d-flex flex-column ${styles.scenarios}`}>
                    {set.scenarios.map((scenario, index) => (
                      <div
                        key={scenario.scenarioLabel}
                        className={classnames('ps-md-2 d-flex w-100 justify-content-between', {
                          [`pb-2 pb-md-3 pe-2 pt-3 pt-xl-0 ${styles.getBackCluster}`]: !index,
                          'pt-2 pt-md-3 pe-2': index,
                        })}
                      >
                        <p
                          className={`${styles.scenarioSubcategoryTitle} ${styles.fieldTextEnhanced}`}
                        >
                          {scenario.scenarioLabel}
                        </p>
                        <div className={`${styles.returnsSection}`}>
                          {scenario.periods.map((period) => (
                            <p
                              key={`${period.performanceTimePeriodID}-value`}
                              className={`${styles.fieldTextEnhanced}`}
                            >
                              {period.value}
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {performanceSets.length === 0 && (
          <div className={`d-flex justify-content-center align-items-center w-100 py-6`}>
            {t('performanceScenarioCalculationsNoResultsMessage')}
          </div>
        )}
      </ModalBody>
    </ModalContent>
  );
};

export default PredictivePerformance;
